import React, { useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import { reloadAfterMutation } from '../../lib/embedSafeReload';
import { MyTeamsModal, useMyTeamsAnchoredModal } from './MyTeamsModal';

interface SwapPlayerModalProps {
  show: boolean;
  mainPlayer: string;
  reservePlayer: string;
  onSwap: () => void;
  onCancel: () => void;
  isProcessing: boolean;
  language: 'en' | 'fr';
  swapType?: 'normal' | 'scratch' | 'scratch-reserve';
  errorMessage?: string | null;
}

export function SwapPlayerModal({
  show,
  mainPlayer,
  reservePlayer,
  onSwap,
  onCancel,
  isProcessing,
  language,
  swapType = 'normal',
  errorMessage = null
}: SwapPlayerModalProps) {
  // Handler to refresh page after successful swap
  const handleSwap = async () => {
    try {
      const result: any = await onSwap();
      // If onSwap returns a result with success, only refresh if success is true or undefined (for backward compatibility)
      if (result === undefined || result?.success === undefined || result?.success) {
        setTimeout(() => {
          reloadAfterMutation();
        }, 1500);
      }
      // else do not refresh (validation failed)
    } catch (e) {
      // Do not refresh on error
    }
  };
  const modalRef = useRef<HTMLDivElement>(null);
  const anchoredModal = useMyTeamsAnchoredModal();

  useEffect(() => {
    if (show && !anchoredModal && modalRef.current) {
      // 👇 On force le scroll vers l'élément
      modalRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [show, anchoredModal]);

  if (!show) return null;

  const titleText = language === 'fr' ? 'Permutation de Joueurs' : 'Swap Players';
  const confirmText = language === 'fr' ? 'Confirmer la Permutation' : 'Confirm Swap';
  const cancelText = language === 'fr' ? 'Annuler' : 'Cancel';
  const processingText = language === 'fr' ? 'Traitement en cours...' : 'Processing...';
  
  // Adapter le message selon le type de permutation
  const getConfirmationText = () => {
    if (swapType === 'scratch-reserve') {
      return language === 'fr' 
        ? `Êtes-vous sûr de vouloir permuter ${mainPlayer} (scratch) avec ${reservePlayer} (réserves)?`
        : `Are you sure you want to swap ${mainPlayer} (scratch) with ${reservePlayer} (reserves)?`;
    } else if (swapType === 'scratch') {
      return language === 'fr' 
        ? `Êtes-vous sûr de vouloir permuter ${mainPlayer} (club principal) avec ${reservePlayer} (scratch players)?`
        : `Are you sure you want to swap ${mainPlayer} (main club) with ${reservePlayer} (scratch players)?`;
    } else {
      return language === 'fr' 
        ? `Êtes-vous sûr de vouloir permuter ${mainPlayer} (club principal) avec ${reservePlayer} (réserves)?`
        : `Are you sure you want to swap ${mainPlayer} (main club) with ${reservePlayer} (reserves)?`;
    }
  };
  
  // Adapter la note selon le type de permutation
  const noteText = swapType === 'scratch'
    ? (language === 'fr'
      ? "Note: Cette action changera instantanément les joueurs."
      : "Note: This action will instantly switch the players.")
    : swapType === 'scratch-reserve'
    ? (language === 'fr'
      ? "Note: Cette action changera instantanément les statuts des joueurs entre scratch et réserves."
      : "Note: This action will instantly switch the players' status between scratch and reserves.")
    : (language === 'fr'
      ? "Note: Cette action changera instantanément les joueurs d'équipe et comptera comme une rétrogradation."
      : "Note: This action will instantly switch the players' teams and count as a demotion.");

  return (
    <MyTeamsModal onClose={onCancel} busy={isProcessing}>
      <div 
        className="modal fade show" 
        style={{ 
          display: 'block',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1050,
          overflow: 'auto'
        }} 
        tabIndex={-1}
      >
        <div className="modal-dialog modal-dialog-centered" ref={modalRef}>
          <div className="modal-content" style={{
              '--text-primary': '#212529',
              '--alert-info-text': '#084298',
              '--alert-info-bg': '#cfe2ff',
              '--alert-info-border': '#9ec5fe',
              '--card-bg': 'rgba(255, 255, 255, 0.97)',
            } as React.CSSProperties}>
            <div className="modal-header">
              <h5 className="modal-title d-flex align-items-center">
                <RefreshCw size={20} className="me-2 text-primary" />
                {titleText}
              </h5>
              <button type="button" className="btn-close" onClick={onCancel}></button>
            </div>
            <div className="modal-body">
              <p>{getConfirmationText()}</p>
              <div className="alert alert-info">
                <small>{noteText}</small>
              </div>
              {errorMessage && (
                <div className="alert alert-danger mt-2 mb-0">
                  <small>{errorMessage}</small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={isProcessing}
              >
                {cancelText}
              </button>
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center"
                onClick={handleSwap}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    {processingText}
                  </>
                ) : (
                  <>
                    <RefreshCw size={16} className="me-2" />
                    {confirmText}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div 
        className="modal-backdrop fade show" 
        style={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040 
        }}
      ></div>
    </MyTeamsModal>
  );
}